import _ from 'underscore'
import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Button } from 'semantic-ui-react'

import AppUtils from '../../../utils/AppUtils'
import AppConstants from '../../../utils/AppConstants'
import ColumnsProvider from '../../../utils/ColumnsProvider'
import Popup from '../../../components/elements/Popup'
import Header from '../../../components/elements/Header'
import Table from '../../../components/elements/Table'
import Confirm from '../../../components/elements/Confirm'
import Dismissible from '../../../components/elements/Dismissible'
import Grant from '../../elements/Grant'

import Types from '../../../actions/Types'
import ListAction from '../../../actions/colors/ListAction'
import PostAction from '../../../actions/colors/PostAction'
import PutAction from '../../../actions/colors/PutAction'
import DeleteAction from '../../../actions/colors/DeleteAction'
import ClearErrorAction from '../../../actions/app/ClearErrorAction'

class ManageColorsPopup extends React.Component {

  constructor(props) {

    // Properties
    super(props)

    // State
    this.state = {
      create: null,
      edit: null,
      delete: null,
      formError: null,
      dirty: false,
      isOpening: true
    }

    // Bind events
    this.onClickCreate = this.onClickCreate.bind(this)
    this.onChangeCreateName = this.onChangeCreateName.bind(this)
    this.onChangeCreateForeground = this.onChangeCreateForeground.bind(this)
    this.onChangeCreateBackground = this.onChangeCreateBackground.bind(this)
    this.onChangeEditName = this.onChangeEditName.bind(this)
    this.onChangeEditForeground = this.onChangeEditForeground.bind(this)
    this.onChangeEditBackground = this.onChangeEditBackground.bind(this)
    this.onClickEdit = this.onClickEdit.bind(this)
    this.onClickDelete = this.onClickDelete.bind(this)
    this.onValidDelete = this.onValidDelete.bind(this)
    this.onClickValid = this.onClickValid.bind(this)
    this.onClickCancel = this.onClickCancel.bind(this)
    this.onDismissFormError = this.onDismissFormError.bind(this)
  }

  componentDidMount() {
    this.props.fetch()
  }

  componentDidUpdate(prevProps, prevState) {
    // When first fetch is success
    if (
      prevProps.listIsFetching
      &&
      !this.props.listIsFetching
      &&
      this.state.isOpening
    ) {
      this.setState({
        isOpening: false
      })
    }

    // When a colour has been created or edited, refresh
    if (
      (
        prevProps.postIsFetching
        &&
        !this.props.postIsFetching
        &&
        !this.props.postError
      )
      ||
      (
        prevProps.putIsFetching
        &&
        !this.props.putIsFetching
        &&
        !this.props.putError
      )
    ) {
      this.setState({
        create: null,
        edit: null,
        dirty: true
      })
      this.props.fetch()
    }

    // When a colour has been deleted, refresh
    if (
      prevProps.deleteIsFetching
      &&
      !this.props.deleteIsFetching
    ) {
      this.setState({
        delete: null
      })
      if (!this.props.deleteError) {
        this.props.fetch()
      }
    }
  }

  onClickCreate() {
    this.setState({
      create: {
        name: '',
        foreground: '000000',
        background: 'FFFFFF'
      }
    })
  }

  onChangeCreateName(name) {
    this.setState((prevState, props) => ({
      create: _.extend(
        {},
        prevState.create,
        {
          name
        }
      )
    }))
  }

  onChangeCreateForeground(hex) {
    this.setState((prevState, props) => ({
      create: _.extend(
        {},
        prevState.create,
        {
          foreground: hex
        }
      )
    }))
  }

  onChangeCreateBackground(hex) {
    this.setState((prevState, props) => ({
      create: _.extend(
        {},
        prevState.create,
        {
          background: hex
        }
      )
    }))
  }

  onChangeEditName(name) {
    this.setState((prevState, props) => ({
      edit: _.extend(
        {},
        prevState.edit,
        {
          name
        }
      )
    }))
  }

  onChangeEditForeground(hex) {
    this.setState((prevState, props) => ({
      edit: _.extend(
        {},
        prevState.edit,
        {
          foreground: hex
        }
      )
    }))
  }

  onChangeEditBackground(hex) {
    this.setState((prevState, props) => ({
      edit: _.extend(
        {},
        prevState.edit,
        {
          background: hex
        }
      )
    }))
  }

  onClickEdit(id) {
    return () => {
      this.setState((prevState, props) => ({
        edit: _.find(props.listData, (color) => (color.id === id))
      }))
    }
  }

  onClickDelete(id) {
    return () => {
      this.setState({
        delete: id
      })
    }
  }

  onValidDelete(proceed) {
    if (proceed) {
      this.props.delete(this.state.delete)
    }
    else {
      this.setState({
        delete: null
      })
    }
  }

  onClickValid() {
    if (this.state.create) {
      if (!_.isString(this.state.create.name) || this.state.create.name.length === 0) {
        this.setState({
          formError: 'Veuillez saisir un nom pour la nouvelle couleur.'
        })
        return
      }
      this.props.post(_.pick(this.state.create, 'name', 'foreground', 'background'))
    }
    else if (this.state.edit) {
      if (!_.isString(this.state.edit.name) || this.state.edit.name.length === 0) {
        this.setState({
          formError: 'Veuillez saisir un nom pour la couleur.'
        })
        return
      }
      this.props.put(
        this.state.edit.id,
        _.pick(this.state.edit, 'name', 'foreground', 'background')
      )
    }
  }

  onClickCancel() {
    this.setState({
      create: null,
      edit: null
    })
  }

  onDismissFormError() {
    this.setState({
      formError: null
    })
  }

  render() {
    const error = this.props.postError || this.props.putError || this.props.deleteError
    return (
      <React.Fragment>
        <Popup
          header='Gérer les couleurs'
          body={
            <div style={{
              maxHeight: '400px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div style={{
                flex: '0 1 auto',
                paddingBottom: '16px'
              }}>
                {
                  error
                    ? <Dismissible
                        header={AppUtils.errorToTitle(error)}
                        content={
                          AppUtils.errorToText(error, {
                            [AppConstants.ERROR.BAD_REQUEST.UNICITY_CONSTRAINT_VIOLATION]: 'Une couleur avec ce nom existe déjà.',
                            [AppConstants.ERROR.BAD_REQUEST.RESOURCE_IN_USE]: 'Cette couleur est utilisée et ne peut pas être supprimée.'
                          })
                        }
                        color='red'
                        onDismiss={this.props.clearError}
                      />
                    : null
                }
                {
                  this.state.formError
                    ? <Dismissible
                        content={this.state.formError}
                        color='orange'
                        onDismiss={this.onDismissFormError}
                      />
                    : null
                }
              </div>
              <Header
                style={{
                  flex: '0 1 auto'
                }}
                title='Couleurs'
                right={
                  <Grant role={Grant.ROLE.MANAGE}>
                    <Button
                      compact={true}
                      icon='plus'
                      content='Créer'
                      color='green'
                      disabled={this.state.create || this.state.edit}
                      onClick={this.onClickCreate}
                    />
                  </Grant>
                }
              />
              <Table
                columns={[
                  // Nom
                  ColumnsProvider.colorName(this),
                  // Texte
                  ColumnsProvider.colorShowAndEdit(
                    this,
                    'foreground',
                    'Texte',
                    this.onChangeCreateForeground,
                    this.onChangeEditForeground
                  ),
                  // Arrière-plan
                  ColumnsProvider.colorShowAndEdit(
                    this,
                    'background',
                    'Arrière-plan',
                    this.onChangeCreateBackground,
                    this.onChangeEditBackground
                  ),
                  // Actions
                  ColumnsProvider.colorActions(this)
                ]}
                data={
                  _.union(
                    this.state.create
                      ? [{
                          id: 'new'
                        }]
                      : [],
                    this.props.listData || []
                  )
                }
                error={this.props.listError}
                isLoading={false}
                emptyMessage="Il n'existe aucune couleur pour le moment."
                float={true}
              />
            </div>
          }
          footer={
            this.state.create || this.state.edit
              ? []
              : [
                  {
                    type: Popup.BUTTONTYPE.CLOSE,
                    title: 'Fermer',
                    icon: 'close',
                    onClick: () => { this.props.onClose(this.state.dirty) }
                  }
                ]
          }
          isLoading={this.state.isOpening}
        />
        {
          this.state.delete
            ? <Confirm
                header="Suppression d'une couleur"
                body="Êtes-vous sûr de vouloir supprimer cette couleur ?"
                confirmText='Supprimer'
                confirmColor='red'
                confirmIcon='trash'
                isProceeding={this.props.deleteIsFetching}
                callback={this.onValidDelete}
              />
            : null
        }
      </React.Fragment>
    )
  }
}

ManageColorsPopup.propTypes = {
  // Properties
  onClose: PropTypes.func.isRequired,

  // Redux State
  listData: PropTypes.array,
  listError: PropTypes.object,
  listIsFetching: PropTypes.bool.isRequired,
  postError: PropTypes.object,
  postIsFetching: PropTypes.bool.isRequired,
  putError: PropTypes.object,
  putIsFetching: PropTypes.bool.isRequired,
  deleteId: PropTypes.number,
  deleteError: PropTypes.object,
  deleteIsFetching: PropTypes.bool.isRequired,

  // Redux Actions
  fetch: PropTypes.func.isRequired,
  post: PropTypes.func.isRequired,
  put: PropTypes.func.isRequired,
  delete: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired
}

export default connect(
  (state) => ({
    listData: state.entities.colors.list.data,
    listError: state.entities.colors.list.error,
    listIsFetching: state.entities.colors.list.isFetching,
    postError: state.entities.colors.post.error,
    postIsFetching: state.entities.colors.post.isFetching,
    putError: state.entities.colors.put.error,
    putIsFetching: state.entities.colors.put.isFetching,
    deleteId: state.entities.colors.delete.id,
    deleteError: state.entities.colors.delete.error,
    deleteIsFetching: state.entities.colors.delete.isFetching
  }),
  (dispatch) => ({
    fetch: () => (dispatch(ListAction({}))),
    post: (data) => (dispatch(PostAction({ data }))),
    put: (id, data) => (dispatch(PutAction({ id, data }))),
    delete: (id) => (dispatch(DeleteAction({ id }))),
    clearError: () => (dispatch(ClearErrorAction(Types.COLORS)))
  })
)(ManageColorsPopup)



// WEBPACK FOOTER //
// ./src/containers/popups/colors/ManageColorsPopup.js